import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const HERO_IMG =
  "https://images.pexels.com/photos/3250359/pexels-photo-3250359.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const LINES = [
  { text: "RIGHT PARTNER.", gold: false },
  { text: "RIGHT PATH.", gold: false },
  { text: "RIGHT GROWTH.", gold: true },
];

const STATS = [
  { value: "$140M+", label: "Client Revenue Scaled" },
  { value: "98.4%", label: "Client Retention" },
  { value: "14", label: "Industry Awards" },
  { value: "250+", label: "Brands Built" },
];

export const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 45, damping: 18 });
  const sy = useSpring(my, { stiffness: 45, damping: 18 });

  const bgX = useTransform(sx, [-0.5, 0.5], [-22, 22]);
  const bgY = useTransform(sy, [-0.5, 0.5], [-14, 14]);
  const fgX = useTransform(sx, [-0.5, 0.5], [14, -14]);
  const fgY = useTransform(sy, [-0.5, 0.5], [10, -10]);

  const onMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <section
      data-testid="hero-section"
      onMouseMove={onMove}
      className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-[#060B18]"
    >
      <motion.div style={{ x: bgX, y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={HERO_IMG}
          alt=""
          className="h-full w-full object-cover opacity-30"
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060B18]/70 via-[#060B18]/40 to-[#060B18]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#060B18] via-transparent to-[#060B18]/60" />
      </motion.div>

      <motion.div
        style={{ x: fgX, y: fgY }}
        aria-hidden="true"
        className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full border border-amber-400/15"
      />
      <motion.div
        style={{ x: fgX, y: fgY }}
        aria-hidden="true"
        className="absolute top-1/3 -left-40 h-[340px] w-[340px] rounded-full bg-amber-400/5 blur-3xl"
      />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pt-40 pb-14">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-amber-400/90 mb-8"
          data-testid="hero-overline"
        >
          Build Right Partners — Marketing Agency — California
        </motion.p>

        <h1 className="font-serif font-semibold tracking-tight leading-[1.02] text-5xl sm:text-7xl lg:text-8xl text-amber-50">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.95, ease: [0.16, 1, 0.3, 1], delay: 0.55 + i * 0.16 }}
                className={`block ${line.gold ? "italic text-transparent bg-clip-text bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11]" : ""}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-8 max-w-xl text-base sm:text-lg font-light leading-relaxed text-slate-300"
          data-testid="hero-subcopy"
        >
          A California-born marketing agency engineering brands the way the Golden Gate was built —
          precisely, boldly, and made to endure.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.3 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollToSection("#contact")}
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11] px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#060B18] font-semibold hover:shadow-[0_0_35px_rgba(212,175,55,0.35)] transition-shadow duration-500"
          >
            Start a Project
            <ArrowDownRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollToSection("#work")}
            className="inline-flex items-center rounded-full border border-amber-400/40 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-amber-200 hover:border-amber-300 hover:bg-amber-400/10 transition-colors duration-300"
          >
            View Our Work
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-amber-500/15 pt-8"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-serif text-3xl sm:text-4xl text-amber-300">{s.value}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
